import React, { useEffect, useState } from 'react';
import { maintenanceAPI, registrationAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Save, AlertTriangle, Shield, UserPlus, UserX } from 'lucide-react';

export const MaintenanceAdmin = () => {
  const [maintenance, setMaintenance] = useState({ is_active: false, message: '' });
  const [registration, setRegistration] = useState({ is_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      maintenanceAPI.get(),
      registrationAPI.get()
    ]).then(([mRes, rRes]) => {
      setMaintenance(mRes.data);
      setRegistration(rRes.data);
    }).catch(() => {
      toast.error('Nepodarilo se nacíst nastavení');
    }).finally(() => setLoading(false));
  }, []);

  const saveMaintenance = async () => {
    setSaving(true);
    try {
      await maintenanceAPI.update(maintenance);
      toast.success(maintenance.is_active ? 'Údržba aktivována' : 'Údržba deaktivována');
    } catch (error) {
      toast.error('Nepodarilo se uložit');
    } finally {
      setSaving(false);
    }
  };

  const saveRegistration = async () => {
    setSaving(true);
    try {
      await registrationAPI.update(registration);
      toast.success(registration.is_enabled ? 'Registrace povoleny' : 'Registrace uzavreny');
    } catch (error) {
      toast.error('Nepodarilo se uložit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">Nastavení webu</h1>
        <p className="text-[#64748B]">Údržba webu a nastavení registrací</p>
      </div>

      {/* Maintenance Mode */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6 max-w-2xl">
        <h2 className="text-lg font-semibold text-[#2C3E50]">Režim údržby</h2>

        <div className={`flex items-center justify-between p-5 rounded-xl ${maintenance.is_active ? 'bg-red-50 border-2 border-red-200' : 'bg-slate-50'}`}>
          <div className="flex items-center gap-3">
            {maintenance.is_active ? (
              <AlertTriangle size={24} className="text-red-500" />
            ) : (
              <Shield size={24} className="text-green-500" />
            )}
            <div>
              <div className="font-semibold text-[#2C3E50]">
                {maintenance.is_active ? 'Údržba je AKTIVNÍ' : 'Web je v provozu'}
              </div>
              <div className="text-sm text-[#64748B]">
                {maintenance.is_active 
                  ? 'Návštevníci vidí hlášku o údržbe' 
                  : 'Návštevníci mají normální prístup'}
              </div>
            </div>
          </div>
          <Switch
            checked={maintenance.is_active}
            onCheckedChange={(checked) => setMaintenance(prev => ({ ...prev, is_active: checked }))}
            data-testid="maintenance-switch"
          />
        </div>

        <div className="space-y-2">
          <Label>Hláška pro návštevníky</Label>
          <Textarea
            value={maintenance.message}
            onChange={(e) => setMaintenance(prev => ({ ...prev, message: e.target.value }))}
            rows={3}
            className="rounded-xl resize-none"
            placeholder="Probíhá údržba webu..."
            data-testid="maintenance-message"
          />
        </div>

        {maintenance.is_active && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertTriangle size={18} className="text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Pozor:</strong> Návštevníci neuvidí žádný obsah webu. Administrace zustává prístupná.
              </div>
            </div>
          </div>
        )}

        <Button onClick={saveMaintenance} disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="save-maintenance-btn">
          <Save size={16} className="mr-2" />
          {saving ? 'Ukládání...' : 'Uložit údržbu'}
        </Button>
      </div>

      {/* Registration Settings */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6 max-w-2xl">
        <h2 className="text-lg font-semibold text-[#2C3E50]">Registrace uživatelu</h2>

        <div className={`flex items-center justify-between p-5 rounded-xl ${registration.is_enabled ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
          <div className="flex items-center gap-3">
            {registration.is_enabled ? (
              <UserPlus size={24} className="text-green-500" />
            ) : (
              <UserX size={24} className="text-red-500" />
            )}
            <div>
              <div className="font-semibold text-[#2C3E50]">
                {registration.is_enabled ? 'Registrace jsou POVOLENY' : 'Registrace jsou UZAVRENY'}
              </div>
              <div className="text-sm text-[#64748B]">
                {registration.is_enabled 
                  ? 'Noví uživatelé se mohou registrovat (po schválení adminem)' 
                  : 'Nikdo se nemůže registrovat'}
              </div>
            </div>
          </div>
          <Switch
            checked={registration.is_enabled}
            onCheckedChange={(checked) => setRegistration(prev => ({ ...prev, is_enabled: checked }))}
            data-testid="registration-switch"
          />
        </div>

        <Button onClick={saveRegistration} disabled={saving} className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="save-registration-btn">
          <Save size={16} className="mr-2" />
          {saving ? 'Ukládání...' : 'Uložit nastavení registrací'}
        </Button>
      </div>
    </div>
  );
};

export default MaintenanceAdmin;
